import Slider from "~/components/ui/Slider";
import SliderJS from "~/components/ui/SliderJS";
import { useId } from "react";
import { ImageWidget } from "~/types/widgets";
import Image from "~/components/ui/Image";
import type { Brand } from "~/components/Header/Brands";
import { sanitizeStoreUrl } from "~/utils/constants";

export interface AlertBarProps {
  /**
   * @title Sliding Alerts
   * @description alerts that will show one at a time
   */
  slidingAlerts: AlertItem[];
  /**
   * @title Autoplay interval
   * @description sliding alerts autoplay interval
   */
  interval?: number;
  /**
   * @title Fixed alerts
   * @description alerts that will be fixed at the side
   */
  fixedAlerts?: AlertItem[];
}

/**
 * @titleBy text
 */
interface AlertItem {
  /**
   * @title Text
   * @description Alert message text (e.g. frete e condições especiais de pagamento)
   */
  text: string;
  icon?: ImageWidget;
  /**
   * @title Icon image description
   */
  iconAlt?: string;
  iconWidth?: number;
  iconHeight?: number;
  invertIconColor?: boolean;
  link?: string;
  textLink?: string;
  newTab?: boolean;
}

function Alert(
  { slidingAlerts, interval, fixedAlerts: _fixedAlerts, brands }:
    & AlertBarProps
    & { brands?: Brand[] },
) {
  const id = useId();

  return (
    <div
      id={id}
      className="relative py-1 h-10 grid grid-cols-[1fr_auto_1fr] items-center leading-[15px] bg-black header-topbar"
    >
      {/* Grid de 3 colunas (1fr / auto / 1fr): a coluna central (o texto do
          alerta) fica sempre centralizada na largura TOTAL da barra, porque
          as colunas laterais dividem igualmente o espaço restante — mesmo
          quando só a coluna da direita tem conteúdo (bloco de marcas, visível
          só em lg+). Antes o texto era centralizado apenas no espaço que
          sobrava depois do bloco de marcas (flex-1 ao lado de um bloco com
          largura própria), o que deixava o alerta visualmente deslocado para
          a esquerda no desktop. A coluna 1 é um spacer vazio que só existe
          para espelhar a largura da coluna 3 e manter a centralização. */}
      <div aria-hidden="true" />
      {slidingAlerts && slidingAlerts.length > 0 && (
        <div className="flex min-w-0 items-center justify-center col-start-2">
          <div className="flex items-center">
            <Slider.PrevButton className="h-6 w-6 flex justify-center items-center">
              <Image
                loading="lazy"
                fetchPriority="low"
                width={8}
                height={15}
                src={"https://decoims.com/danielatombini/b1804050-8518-4deb-ab26-ec3c0e637496/fe3977df-81e5-4fd6-9861-a742a1a37ac1___a221a1bcc3ca23c3360e8c2e26f04362.png"}
                alt="Previous item"
              />
            </Slider.PrevButton>
          </div>
          <Slider className="carousel carousel-center max-w-[500px]">
            {slidingAlerts.map((alert, index) => (
              <Slider.Item
                index={index}
                className="carousel-item w-full items-center justify-center gap-2.5"
              >
                {alert.icon && (
                  <Image
                    loading="lazy"
                    fetchPriority="low"
                    src={alert.icon}
                    alt={alert.iconAlt}
                    width={alert.iconWidth ?? 17}
                    height={alert.iconHeight ?? 17}
                    className={alert.invertIconColor ? "invert" : ""}
                  />
                )}

                <span className="font-['Poppins'] not-italic font-normal text-xs lg:text-[13px] leading-[16px] tracking-[0.06em] text-white">
                  {alert.text}
                </span>
                {alert.textLink && (
                  <a
                    href={alert.link}
                    target={alert.newTab ? "_blank" : "_self"}
                    className="font-['Poppins'] not-italic font-normal text-xs lg:text-[13px] leading-[16px] tracking-[0.06em] text-white underline"
                  >
                    {alert.textLink}
                  </a>
                )}
              </Slider.Item>
            ))}
          </Slider>
          <div className="flex items-center">
            <Slider.NextButton className="h-6 w-6 flex justify-center items-center">
              <Image
                loading="lazy"
                fetchPriority="low"
                width={8}
                height={15}
                src={"https://decoims.com/danielatombini/d019ef56-25e5-44bc-bbe9-cca2ae098f8c/843aa847-35c4-4741-9d76-aea4b7b423c9___5cc61303fbfd732772f1d2563b9b6ae7.png"}
                alt="Next item"
              />
            </Slider.NextButton>
          </div>

          <SliderJS
            rootId={id}
            interval={interval && interval * 1e3}
            infinite
          />
        </div>
      )}
      {brands && brands.length > 0 && (
        <div className="hidden lg:flex col-start-3 shrink-0 ml-auto items-center gap-3 pr-4">
          {brands.map((brand, index) => {
            const logoSrc = brand.logo?.src;
            if (!logoSrc) return null;

            /** o primeiro logo (marca principal) é empilhado, por isso renderiza maior */
            const isMainBrand = index === 0;

            return (
              <a
                key={brand.url}
                href={sanitizeStoreUrl(brand.url)}
                className="flex items-center"
              >
                <Image
                  loading="lazy"
                  fetchPriority="low"
                  src={logoSrc}
                  alt={brand.logo?.alt}
                  width={brand.logoWidth ?? 60}
                  height={brand.logoHeight ?? 18}
                  className={`w-auto object-contain brightness-0 invert ${
                    isMainBrand ? "h-[30px]" : "h-[18px]"
                  }`}
                />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Alert;
